import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import clanService from '../../services/clanService';
import { adaptClanGamesToMissionHistory } from '../../utils/clanGameAdapter';
import MissionHistoryList from '../mission/MissionHistoryList';
import MissionHistoryFilters from '../mission/MissionHistoryFilters';
import MissionHistoryPagination from '../mission/MissionHistoryPagination';
import { MissionHistory, MissionHistoryFilters as MissionHistoryFiltersType } from '../../types/missionHistory';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isModernTheme = (theme: any) => theme.name === 'modernWarfare';

const GameTypeTabs = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const GameTypeTab = styled.button<{ $active: boolean }>`
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  font-size: 12px;
  padding: 8px 16px;
  background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.1)};
  color: ${props => props.$active ? (props.theme.name === 'shadowGrid' ? '#000000' : '#ffffff') : props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  transition: all 0.3s ease;
  border-radius: ${({ theme }) => isModernTheme(theme) ? '2px' : '6px'};
  
  ${({ theme }) => isModernTheme(theme) && `
    clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
    margin-right: 5px;
    border: none;
  `}
  
  ${({ theme }) => !isModernTheme(theme) && `
    &:first-child {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
      border-right: none;
    }
    
    &:last-child {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      border-left: none;
    }
    
    &:not(:first-child):not(:last-child) {
      border-right: none;
    }
  `}
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.2)};
    box-shadow: 0 0 10px ${props => withAlpha(props.theme.colors.primary, 0.3)};
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

interface ClanGamesSectionProps {
  clanId: string;
  clanName?: string;
  supportedGameTypes?: string[];
  showFilters?: boolean;
  showPagination?: boolean;
  className?: string;
}

const ClanGamesSection: React.FC<ClanGamesSectionProps> = ({
  clanId,
  clanName = 'Unknown Clan',
  supportedGameTypes = ['airsoft', 'paintball', 'speedball'],
  showFilters = true,
  showPagination = true,
  className
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeGameType, setActiveGameType] = useState<'airsoft' | 'paintball' | 'speedball'>('airsoft');
  const [games, setGames] = useState<MissionHistory[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState<string | null>(null);
  
  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize] = useState(10);
  
  // Фильтры
  const [gameFilters, setGameFilters] = useState<MissionHistoryFiltersType>({});

  const loadGames = async (
    gameType: 'airsoft' | 'paintball' | 'speedball' = 'airsoft',
    filters: MissionHistoryFiltersType = {},
    page: number = 1
  ) => {
    if (!clanId) return;

    try {
      setGamesLoading(true);
      setGamesError(null);
      
      // Готовим параметры для серверной фильтрации
      const options = {
        page,
        pageSize,
        specificDate: filters.specificDate ? filters.specificDate.toISOString().split('T')[0] : undefined
      };
      
      const response = await clanService.getClanGames(clanId, gameType, options);
      
      // Адаптируем данные к формату MissionHistory
      const adaptedGames = adaptClanGamesToMissionHistory(response.games, clanName, 'Clan Referee', clanId);
      
      setGames(adaptedGames);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setHasMore(response.hasMore);
      setCurrentPage(response.page);
    } catch (error: any) {
      setGamesError('Failed to load games');
      console.error('Error loading games:', error);
    } finally {
      setGamesLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: MissionHistoryFiltersType) => {
    setGameFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadGames(activeGameType, gameFilters, 1);
  };

  const handleClearFilters = () => {
    const emptyFilters: MissionHistoryFiltersType = {};
    setGameFilters(emptyFilters);
    setCurrentPage(1);
    loadGames(activeGameType, emptyFilters, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadGames(activeGameType, gameFilters, page);
  };

  const handleGameClick = (game: MissionHistory) => {
    navigate(`/missions/${activeGameType}/${game.id}`);
  };

  // Загружаем игры при изменении активного типа игры
  useEffect(() => {
    if (clanId) {
      setCurrentPage(1);
      loadGames(activeGameType, gameFilters, 1);
    }
  }, [activeGameType, clanId]);

  return (
    <div className={className}>
      {/* Кнопки переключения типов игр */}
      <GameTypeTabs>
        {supportedGameTypes.map(gameType => (
          <GameTypeTab
            key={gameType}
            $active={activeGameType === gameType}
            onClick={() => setActiveGameType(gameType as any)}
          >
            {gameType.charAt(0).toUpperCase() + gameType.slice(1)}
          </GameTypeTab>
        ))}
      </GameTypeTabs>
      
      <SectionContent>
        {/* Фильтры */}
        {showFilters && (
          <MissionHistoryFilters
            filters={gameFilters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
          />
        )}
        
        {/* Список игр */}
        <MissionHistoryList
          missions={games}
          loading={gamesLoading}
          error={gamesError}
          onMissionClick={handleGameClick}
          showResultColumn={false}
          showPlayerFaction={false}
          factionLabel="WIN FACTION"
        />
        
        {/* Пагинация */}
        {showPagination && !gamesLoading && !gamesError && totalCount > 0 && (
          <MissionHistoryPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            hasMore={hasMore}
            onPageChange={handlePageChange}
          />
        )}
      </SectionContent>
    </div>
  );
};

export default ClanGamesSection;